import { Button, Stack, Typography } from "@mui/material";
import React from "react";
import Modal, { CommonModalProps } from "..";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import { GetSectionIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppSelector } from "../../../Redux/redux-store";
import { DbMarkerStatus } from "../../../Core/Api/Types/db-marker-status";
import { useTranslation } from "react-i18next";

export type ModalMarkerActionElementProps = {
  context: "Bypass" | "Comment" | "FixIt" | "Fixed";
  markerId: number;
  onConfirm?: (markerId: number, newStatus?: DbMarkerStatus, comment?: string) => void;
};

export type ModalMarkerActionProps = CommonModalProps<ModalMarkerActionElementProps>;

const ModalMarkerAction = React.forwardRef<any, ModalMarkerActionProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, onConfirm, ...modalProps } = props;
  const { markerId, context } = modalProps;
  const [commentValue, setCommentValue] = React.useState<string>();
  const [newStatus, setNewStatus] = React.useState<DbMarkerStatus | undefined>(
    context === "Bypass"
      ? DbMarkerStatus.Bypassed
      : context === "FixIt"
      ? DbMarkerStatus.TobeFixed
      : context === "Fixed"
      ? DbMarkerStatus.Fixed
      : undefined
  );
  const isValid = useAppSelector(GetSectionIsValid("form-issue-action", "sectionComment"));

  const updateInputValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCommentValue(event.target.value);
  };

  const handleConfirmClick = () => {
    onConfirm && onConfirm(markerId, newStatus, commentValue);
    closeModal();
  };

  const handleCancelClick = () => {
    closeModal();
  };

  return (
    <Modal
      title={
        context === "Bypass"
          ? t("modal:title.bypassThisIssue")
          : context === "Comment"
          ? t("modal:title.addComment")
          : context === "FixIt"
          ? t("modal:title.fixIssue")
          : t("modal:title.issueFixed")
      }
      reference={ref}
      closeModal={handleCancelClick}
      width={600}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button color="success" variant="outlined" disabled={!isValid} onClick={handleConfirmClick}>
            {t("common:confirm")}
          </Button>
        </Stack>
      }
    >
      <Stack>
        <Stack spacing={2} mb={2}>
          <Typography variant="h6" component="span" color="text.secondary">
            {t("common:comment")}
          </Typography>
          <FormInputText
            multiline
            sx={{ width: "100%" }}
            value={commentValue}
            onChange={updateInputValue}
            placeholder={t("field:placeholder.comment")}
            validationProps={{
              formId: "form-issue-action",
              formSectionId: "sectionComment",
              fieldId: "comment",
              initialState: false,
              required: context === "Bypass" || context === "Comment",
              minLength: 3,
            }}
          />
        </Stack>
      </Stack>
    </Modal>
  );
});

export default ModalMarkerAction;
